import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { leadsApi } from "../../../libs/landvoice-api";
import { Lead } from "../../../types/neighborhoodSearch";
import { generateSearchListingsQueryKey } from "../utils/generateSearchListingsQueryKey";
import {
  BEDS_MIN,
  BEDS_MAX,
  BATHS_MIN,
  BATHS_MAX,
  ACREAGE_MIN,
  ACREAGE_MAX,
  SQFT_MIN,
  SQFT_MAX,
  YEAR_BUILT_MIN,
} from "src/constants/neighborhoodSearch";
import { getMinMaxQueryParams } from "../utils/getMinMaxQueryParams";
import { convertPrivatelyOwnedToCompanyOwned, getYearBuiltMax } from "../../../utils/neighborhoodSearch";
import { isNullOrUndefined } from "src/utils/objects";

interface UseExportListingsQueryParams {
  selectedIds?: string[];
  beds?: [number | null, number | null];
  baths?: [number | null, number | null];
  acreage?: [number | null, number | null];
  sqft?: [number | null, number | null];
  yearBuilt?: [number | null, number | null];
  selectedPropertyTypes?: string[];
  isShowOnlyPrivatelyOwned?: boolean | null | undefined;
  isFavorite?: boolean | null | undefined;
  searchByTags?: string[] | string;
  searchByNotes?: string[] | string;
}

export const useExportListingsQuery = ({
  selectedIds = [],
  beds = undefined,
  baths = undefined,
  acreage = [null, null],
  sqft = [null, null],
  yearBuilt = [null, null],
  selectedPropertyTypes = [],
  isShowOnlyPrivatelyOwned = undefined,
  isFavorite = undefined,
  searchByTags = [],
  searchByNotes = [],
}: UseExportListingsQueryParams): UseQueryResult<{
  leads: Lead[];
  total_records: number;
}> => {
  // Prepare query parameters exactly like useSearchListingsNS
  const queryParams = {
    listId: selectedIds,
    bedrooms: isNullOrUndefined(beds) ? undefined : [beds[0]?.toString() || "", beds[1]?.toString() || ""],
    bathrooms: isNullOrUndefined(baths) ? undefined : [baths[0]?.toString() || "", baths[1]?.toString() || ""],
    acreage: [acreage?.[0]?.toString() || "", acreage?.[1]?.toString() || ""],
    squareFootage: [sqft?.[0]?.toString() || "", sqft?.[1]?.toString() || ""],
    yearBuilt: [yearBuilt?.[0]?.toString() || "", yearBuilt?.[1]?.toString() || ""],
    propertyType: selectedPropertyTypes,
    companyOwned: convertPrivatelyOwnedToCompanyOwned(isShowOnlyPrivatelyOwned),
    favorite: isFavorite,
    tag: Array.isArray(searchByTags) ? searchByTags.join(",") : (searchByTags || ""),
    note: Array.isArray(searchByNotes) ? searchByNotes.join(",") : (searchByNotes || ""),
  };

  // Create the export query key with queryType: "export"
  const exportQueryKey = generateSearchListingsQueryKey({
    ...queryParams,
    queryType: "export",
  });

  return useQuery({
    queryKey: exportQueryKey,
    enabled: false, // Only run when explicitly called
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await leadsApi.getLeadsWithCount(
        queryParams.bedrooms ? getMinMaxQueryParams(
          Number(queryParams.bedrooms[0]),
          Number(queryParams.bedrooms[1]),
          BEDS_MIN,
          BEDS_MAX
        ) : undefined,
        queryParams.bathrooms ? getMinMaxQueryParams(
          Number(queryParams.bathrooms[0]),
          Number(queryParams.bathrooms[1]),
          BATHS_MIN,
          BATHS_MAX
        ) : undefined,
        getMinMaxQueryParams(
          Number(queryParams.acreage[0]),
          Number(queryParams.acreage[1]),
          ACREAGE_MIN,
          ACREAGE_MAX
        ),
        getMinMaxQueryParams(
          Number(queryParams.squareFootage[0]),
          Number(queryParams.squareFootage[1]),
          SQFT_MIN,
          SQFT_MAX
        ),
        getMinMaxQueryParams(
          Number(queryParams.yearBuilt[0]),
          Number(queryParams.yearBuilt[1]),
          YEAR_BUILT_MIN,
          getYearBuiltMax()
        ),
        queryParams.propertyType,
        queryParams.favorite,
        queryParams.tag,
        queryParams.note,
        queryParams.companyOwned,
        undefined, // showHidden
        undefined, // onlyHidden
        queryParams.listId,
        0, // Always start at page 0 for export
        10000 // Always use large page size for export
      );
      return {
        leads: data.leads || [],
        total_records: data.total_count,
      };
    },
  });
};